import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { getMainSiteUrl } from '@/lib/domain';
import { THEME_BASE_CSS, THEME_SCOPE_CSS } from '@/lib/themeBaseCss';
import { isStyledTheme } from '@/lib/themeUsability';
import TenantThemeShell from './TenantThemeShell';
import TenantHome from './TenantHome';
import ProduitsPublic from '@/pages/public/ProduitsPage';
import HebergementsPage from '@/pages/public/HebergementsPage';
import DetailHebergementPage from '@/pages/public/DetailHebergementPage';
import EventsPage from '@/pages/public/EventsPage';
import DetailEventPage from '@/pages/public/DetailEventPage';
import FormationsPage from '@/pages/public/FormationsPage';
import DetailFormationPage from '@/pages/public/DetailFormationPage';
import TenantSiteGate from '@/components/feature/TenantSiteGate';
import TenantColorBridge from '@/components/feature/TenantColorBridge';
import DetailProduitPage from '@/pages/public/DetailProduitPage';
import DetailAnnoncePage from '@/pages/public/DetailAnnoncePage';
import RecherchePublic from '@/pages/public/RecherchePage';
import ServicesPublic from '@/pages/public/ServicesPage';
import PartenairesPublic from '@/pages/public/PartenairesPage';
import PortfolioPublic from '@/pages/public/PortfolioPage';
import EquipePublic from '@/pages/public/EquipePage';
import PanierPublic from '@/pages/public/PanierPage';
import CommandePublic from '@/pages/public/CommandePage';
import BookingPage from '@/pages/public/BookingPage';
import PublicFormPage from '@/pages/public/FormPage';
import ZCallRoomPage from '@/pages/public/ZCallRoomPage';
import NotFound from '@/pages/NotFound';
import TenantThemePage from './TenantThemePage';
import VendeurRegisterPage from './VendeurRegisterPage';
import { TenantLoginClientPage, TenantRegisterClientPage } from './TenantClientAuth';
import VendeursPage from '@/pages/public/VendeursPage';
import CategoriesPage from '@/pages/public/CategoriesPage';
import AnnoncesPage from '@/pages/public/AnnoncesPage';
import PublierAnnoncePage from '@/pages/public/PublierAnnoncePage';
import MarketplaceCatalogPage from '@/pages/public/MarketplaceCatalogPage';
import CategorySellersPage from '@/pages/public/CategorySellersPage';
import VendorShopPage from '@/pages/public/VendorShopPage';
import VendeurEspacePage from './vendeur/page';
import VendorAppHost from './vendeur/VendorAppHost';
import FacebookPixel from '@/components/feature/FacebookPixel';
import TenantVisitTracker, { ProductViewTracker } from '@/components/feature/TenantVisitTracker';
import { getStoreCapabilities } from '@/lib/accountType';
import CustomPage from '@/pages/public/CustomPage';
import MyLinksPage from './MyLinksPage';
import { TenantBlogPage, TenantBlogArticlePage } from './TenantBlog';

// Pages gérées par le thème (rendues depuis sitewebthemepage) accessibles
// via leurs propres chemins « classiques » utilisés par les gabarits.
const THEME_ALIAS_PAGES: { path: string; key: string }[] = [
  { path: '/apropos', key: 'apropos' },
  { path: '/contact', key: 'contact' },
  { path: '/faq', key: 'faq' },
  { path: '/gallery', key: 'gallery' },
  { path: '/galerie', key: 'gallery' },
  { path: '/project', key: 'project' },
  { path: '/equipe', key: 'equipe' },
  { path: '/partenaires', key: 'partenaires' },
  { path: '/marketplace', key: 'marketplace' },
  { path: '/conditionsdutilisation', key: 'conditionsdutilisation' },
  { path: '/politique', key: 'politique' },
  { path: '/retours', key: 'retours' },
];

/** Charge les polices du socle de thème (Inter + Playfair Display). */
function TenantFonts({ extraFont }: { extraFont?: string }) {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];

    const addFont = (href: string) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      document.head.appendChild(el);
      links.push(el);
    };

    addFont('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap');

    if (extraFont) {
      const family = extraFont.trim().replace(/\s+/g, '+');
      if (family) {
        addFont(`https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700;800&display=swap`);
      }
    }

    return () => links.forEach((el) => el.remove());
  }, [extraFont]);

  return null;
}

export default function TenantStore() {
  const { tenant, theme, loading } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500">Chargement de la boutique...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-store-2-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Boutique introuvable</h2>
        <p className="text-sm text-foreground-500 text-center max-w-md mb-6">
          Cette boutique n&apos;existe pas ou a &eacute;t&eacute; d&eacute;sactiv&eacute;e.
        </p>
        <a href={getMainSiteUrl()} className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer no-underline whitespace-nowrap">
          Retour &agrave; Zifek
        </a>
      </div>
    );
  }

  // Les @import ne sont pas valides dans une couche CSS : on les retire
  // (les polices du thème sont chargées séparément par <TenantFonts/>).
  const themeSheet = (theme?.stylesheet || '').replace(/@import[^;]+;/gi, '');

  // Le type de compte détermine les sections autorisées sur le site public.
  const caps = getStoreCapabilities(tenant.type);

  // Si le thème fournit sa propre feuille de style, on laisse le thème gérer
  // le fond (body) ; sinon on applique le fond clair par défaut.
  const hasThemeStylesheet = isStyledTheme(theme?.stylesheet);

  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <div className={`min-h-screen flex flex-col ${hasThemeStylesheet ? '' : 'bg-background-50'}`}>
        {/* Socle de classes du thème (hérité si le thème ne les redéfinit pas).
            ⚠️ Injecté dans une couche CSS dédiée : ainsi les règles génériques
            du thème (h1…h6, p, a, .card, .container…) ne peuvent JAMAIS écraser
            les classes Tailwind des pages de l'application (titres énormes,
            cartes géantes, barres de navigation déformées). */}
        <style>{`@layer zifek-theme {${THEME_BASE_CSS}}`}</style>

        {/* Charte graphique du tenant (couleur principale → variables --chart-*) */}
        {theme?.chartPaletteCss && (
          <style>{theme.chartPaletteCss}</style>
        )}

        {/* Feuille de style du thème (surcharge le socle), elle aussi isolée
            dans la même couche : le thème garde la main sur ses propres
            gabarits, mais pas sur l'interface de l'application. */}
        {themeSheet && (
          <style>{`@layer zifek-theme {${themeSheet}}`}</style>
        )}

        {/* Typographie des gabarits du thème, strictement limitée à
            .zifek-theme-scope (contenu injecté par le thème). */}
        <style>{THEME_SCOPE_CSS}</style>

        <TenantFonts extraFont={theme?.fontFamily} />

        {/* Couleurs du commerce appliquées au design natif (boutons, liens, accents) */}
        <TenantColorBridge />

        {/* Facebook Pixel — suivi des visiteurs sur TOUTES les pages publiques (si configuré) */}
        <FacebookPixel />

        {/* Comptage des visites du site public (alimente les statistiques du marchand) */}
        <TenantVisitTracker />

        <TenantSiteGate>
          <TenantThemeShell>
          <Routes>
            <Route path="/" element={<TenantHome />} />
            <Route path="/search" element={<RecherchePublic />} />
            {caps.products && <Route path="/products" element={<ProduitsPublic />} />}
            <Route path="/produits" element={<ProduitsPublic />} />
            <Route path="/hebergements" element={<HebergementsPage />} />
            <Route path="/hebergement/:id" element={<DetailHebergementPage />} />
            <Route path="/evenements" element={<EventsPage />} />
            <Route path="/evenement/:id" element={<DetailEventPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<DetailEventPage />} />
            <Route path="/formations" element={<FormationsPage />} />
            <Route path="/formation/:id" element={<DetailFormationPage />} />
            {caps.products && <Route path="/product/:id" element={<><ProductViewTracker /><DetailProduitPage /></>} />}
            <Route path="/produit/:id" element={<><ProductViewTracker /><DetailAnnoncePage /></>} />
            <Route path="/cart" element={<PanierPublic />} />
            <Route path="/checkout" element={<CommandePublic />} />
            {caps.services && <Route path="/services" element={<ServicesPublic />} />}
            <Route path="/service" element={<ServicesPublic />} />
            {caps.services && <Route path="/service/:id" element={<DetailAnnoncePage />} />}
            <Route path="/annonce/:id" element={<DetailAnnoncePage />} />
            <Route path="/partners" element={<PartenairesPublic />} />
            <Route path="/portfolio" element={<PortfolioPublic />} />
            <Route path="/team" element={<EquipePublic />} />
            {caps.services && <Route path="/booking" element={<BookingPage />} />}
            {caps.services && <Route path="/booking/:serviceId" element={<BookingPage />} />}
            {caps.services && <Route path="/reservation" element={<BookingPage />} />}
            <Route path="/forms/:id" element={<PublicFormPage />} />
            <Route path="/call/:roomId" element={<ZCallRoomPage />} />
            <Route path="/zcall/:slug" element={<ZCallRoomPage />} />
            <Route path="/page/:pageKey" element={<TenantThemePage />} />
            <Route path="/page/blog" element={<TenantBlogPage />} />
            <Route path="/blog" element={<TenantBlogPage />} />
            <Route path="/blog/:slug" element={<TenantBlogArticlePage />} />
            <Route path="/p/:slug" element={<CustomPage />} />
            {THEME_ALIAS_PAGES.map((alias) => (
              <Route key={alias.path} path={alias.path} element={<TenantThemePage pageKey={alias.key} />} />
            ))}
            <Route path="/vendeur/inscription" element={<VendeurRegisterPage />} />
            {/* Connexion & inscription client : restent SUR la marketplace */}
            <Route path="/connexion" element={<TenantLoginClientPage />} />
            <Route path="/login-client" element={<TenantLoginClientPage />} />
            <Route path="/register-client" element={<TenantRegisterClientPage />} />
            <Route path="/vendeur/espace" element={<VendeurEspacePage />} />
            <Route path="/vendor-dashboard" element={<VendeurEspacePage />} />
            <Route path="/vendor-dashboard/app/:slug" element={<VendorAppHost />} />
            <Route path="/vendeurs" element={<VendeursPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/annonces" element={<AnnoncesPage />} />
            <Route path="/publier-annonce" element={<PublierAnnoncePage />} />
            <Route path="/marketplace/produits" element={<MarketplaceCatalogPage annonceType="produits" />} />
            <Route path="/marketplace/services" element={<MarketplaceCatalogPage annonceType="services" />} />
            <Route path="/marketplace/categorie/:id" element={<CategorySellersPage />} />
            <Route path="/boutique/:id" element={<VendorShopPage />} />
            <Route path="/mylinks" element={<MyLinksPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </TenantThemeShell>
        </TenantSiteGate>
      </div>
    </BrowserRouter>
  );
}